/**
 * PlaceholderPage — generic "coming soon" page for features that have a route
 * and a sidebar entry but aren't implemented yet.
 *
 * Each placeholder shows:
 * - The feature name (large, with icon)
 * - The phase it's planned for (badge)
 * - A short description of what it will do
 * - A bullet list of features that will be in it (extracted from v12 mockup)
 * - A "back to dashboard" link
 *
 * Used by ~22 routes in router.tsx during Phase 4.2-4.10. As each phase
 * delivers, the corresponding placeholder is replaced with the real page.
 */
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Sparkles, LockKeyhole, type LucideIcon } from "lucide-react";

type PlaceholderPageProps = {
  /** Lucide icon shown next to title */
  icon: LucideIcon;
  /** i18n key for the feature name (e.g. "nav.campaigns") */
  titleKey: string;
  /** Phase tag like "Phase 4.5" — shown as a badge */
  phase: string;
  /** i18n key for the description paragraph */
  descriptionKey: string;
  /** i18n keys for the feature bullet list (3-6 items) */
  featureKeys: string[];
};

export default function PlaceholderPage({
  icon: Icon,
  titleKey,
  phase,
  descriptionKey,
  featureKeys,
}: PlaceholderPageProps) {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-3xl py-8">
      {/* Back link */}
      <Link
        to="/dashboard"
        className="mb-6 inline-flex items-center gap-1.5 text-caption text-ink-muted transition-colors hover:text-ink"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        {t("placeholder.back_to_dashboard")}
      </Link>

      {/* Header */}
      <div className="mb-2 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
          <Icon className="h-5 w-5 stroke-[1.75]" aria-hidden="true" />
        </div>
        <div>
          <h1 className="text-display-sm font-semibold tracking-tight text-ink">
            {t(titleKey)}
          </h1>
        </div>
      </div>

      {/* Phase badge */}
      <div className="mb-6 inline-flex items-center gap-1.5 rounded-pill bg-predict-100 px-3 py-1 text-micro font-semibold uppercase tracking-wider text-predict-700">
        <Sparkles className="h-3 w-3" aria-hidden="true" />
        {t("placeholder.coming_in", { phase })}
      </div>

      {/* Access guard (PO 27-Jul): every placeholder states explicitly that
          the feature is not accessible for this account yet. Central here,
          so ALL placeholder routes get it with one change. */}
      <div className="mb-6 flex items-start gap-2.5 rounded-xl bg-amber-50 p-4 ring-1 ring-amber-200/70">
        <LockKeyhole className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden="true" />
        <div>
          <p className="text-[14px] font-semibold text-amber-900">
            {t("placeholder.no_access")}
          </p>
          <p className="mt-0.5 text-[13px] leading-relaxed text-amber-800/90">
            {t("placeholder.no_access_hint")}
          </p>
        </div>
      </div>

      {/* Description */}
      <p className="mb-8 text-body leading-relaxed text-ink-muted">
        {t(descriptionKey)}
      </p>

      {/* Feature list */}
      <div className="rounded-lg border border-border bg-surface-card p-6">
        <h2 className="mb-4 text-caption font-semibold uppercase tracking-wider text-ink-subtle">
          {t("placeholder.what_youll_be_able_to_do")}
        </h2>
        <ul className="space-y-3">
          {featureKeys.map((key) => (
            <li key={key} className="flex items-start gap-2.5 text-body text-ink">
              <span
                className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500"
                aria-hidden="true"
              />
              <span>{t(key)}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Footer note */}
      <p className="mt-6 text-caption text-ink-subtle">
        {t("placeholder.footer_note")}
      </p>
    </div>
  );
}
